import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { useLook } from '@/look/store';
import { cardModel, placeText, renderCard, type ShareFacts } from './card';
import { canShareFile, copyText, shareFile, shareText } from './share';

/** Each said once: the heading says what the card is for, so the hint and the buttons stay short. */
const STORY = {
  title: 'For your story', share: 'Share', hold: 'Press and hold the card to save it.',
  // 9 Oct: the card carries no address, so the link travels on its own. 14 Oct: the link is the player's own invite
  // (the share chain), and says what a friend gets: the next battle, and a score to beat.
  sticker: 'Send friends your link. They join the next battle and try to beat you.', copy: 'Copy my link', copied: 'Link copied',
} as const;

/** Selects an element's text, so it can be copied by hand where the clipboard is refused. */
function selectAll(el: HTMLElement | null) {
  if (!el) return;
  try { const sel = window.getSelection(); const r = document.createRange(); r.selectNodeContents(el); sel?.removeAllRanges(); sel?.addRange(r); } catch { /* nothing to select with */ }
}

/**
 * M11: the story card preview, a Share button that hands the image to the phone (or, where it cannot, the hint to press
 * and hold the card to save it), then a line asking for a link sticker with a Copy link button for the join link (9 Oct:
 * the card itself carries no address). The copy happens in the tap; where the clipboard is refused (some in-app
 * browsers), the link is shown selected so it can be copied by hand. Tapping the preview shows the card full size, which
 * is easier to press and hold (in Instagram's browser too). The card is drawn in the look on the page (the battle's, or
 * a preview), and drawn again if the look changes.
 */
export function ShareCard({ facts, url, className = '', onShared, note }: { facts: ShareFacts; url: string; className?: string; onShared?: () => void; note?: string }) {
  const [img, setImg] = useState<string>(); const [file, setFile] = useState<File>(); const [failed, setFailed] = useState(false);
  const [hint, setHint] = useState(false); const [copied, setCopied] = useState(false); const [big, setBig] = useState(false);
  // The clipboard said no: the link is shown and selected for copying by hand.
  const [manual, setManual] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null); const linkRef = useRef<HTMLSpanElement>(null);
  const { look } = useLook();
  const key = JSON.stringify(facts);
  useEffect(() => {
    let live = true; let objectUrl: string | undefined;
    renderCard(cardModel(facts), look).then((blob) => {
      if (!live) return;
      if (!blob) { setFailed(true); return; }
      try { objectUrl = URL.createObjectURL(blob); } catch { setFailed(true); return; }
      setImg(objectUrl); setFile(new File([blob], 'medsnapp-rounds-result.png', { type: 'image/png' })); setFailed(false);
    });
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [key, look]); // eslint-disable-line react-hooks/exhaustive-deps

  // The full-size view takes the focus and closes on Escape.
  useEffect(() => {
    if (!big) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);

  const text = shareText({ rank: facts.rank, players: facts.players, title: facts.title, url });
  const supported = file ? canShareFile(file) : false;
  async function share() {
    if (!file) return;
    const outcome = await shareFile(file, text);
    if (outcome === 'shared') onShared?.();
    if (outcome === 'unsupported' || outcome === 'failed') setHint(true);
  }
  // The clipboard is written in the tap itself (copyText calls it before any await), as browsers require.
  async function copy() { onShared?.(); if (await copyText(url)) { setCopied(true); setManual(false); } else { setManual(true); selectAll(linkRef.current); } }
  // Shown after a refused copy: selected as soon as it is on screen (a second refusal selects it again, above).
  useEffect(() => { if (manual) selectAll(linkRef.current); }, [manual]);
  const alt = `Your result card: ${facts.rank ? `${placeText(facts.rank, facts.players)}, ` : ''}${facts.score.toLocaleString('en-GB')} points`;

  return (
    <section aria-labelledby="share-title" data-part="share" className={`flex gap-4 rounded-card bg-surface p-4 shadow-card ${className}`}>
      {!failed && (
        <div data-part="share-preview" className="w-[38%] max-w-40 shrink-0">
          {img ? (
            <button type="button" onClick={() => setBig(true)} aria-label="Show the card full size" className="block w-full rounded-tile">
              <img src={img} alt={alt} className="aspect-[9/16] w-full rounded-tile border border-line object-cover shadow-card" />
            </button>
          ) : <div aria-hidden="true" className="aspect-[9/16] w-full animate-pulse rounded-tile bg-surface-2" />}
        </div>
      )}
      <div data-part="share-actions" className="flex min-w-0 flex-1 flex-col justify-center gap-3">
        <h2 id="share-title" className="font-display text-xl font-extrabold leading-tight text-ink">{STORY.title}</h2>
        {supported && <Button type="button" onClick={() => void share()}>{STORY.share}</Button>}
        {img && (!supported || hint) && <p className="text-sm font-semibold text-ink-2">{STORY.hold}</p>}
        <div data-part="share-link" className="flex flex-col gap-2">
          <p className="text-sm text-ink-2">{STORY.sticker}</p>
          <Button type="button" variant="quiet" size="md" onClick={() => void copy()}>{copied ? STORY.copied : STORY.copy}</Button>
          {manual && <span ref={linkRef} data-part="share-link-text" className="select-all break-all text-sm font-semibold text-ink">{url}</span>}
        </div>
        {note && <p data-part="share-note" className="text-sm font-semibold text-brand-strong">{note}</p>}
      </div>

      {big && img && (
        <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Your result card, full size" onClick={() => setBig(false)}
          className="fixed inset-0 z-50 outline-none flex flex-col items-center justify-center gap-4 bg-ink/85 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))]">
          <img src={img} alt={alt} onClick={(e) => e.stopPropagation()} className="max-h-[76dvh] w-auto max-w-full rounded-card shadow-pop" />
          <p className="max-w-xs text-center text-sm font-semibold text-white">{STORY.hold}</p>
          <Button type="button" variant="quiet" size="md" className="w-auto" onClick={() => setBig(false)}>Close</Button>
        </div>
      )}
    </section>
  );
}
